import { useEffect, useState } from "react";
import {
  Box,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  Stack,
  Typography,
  styled
} from "@mui/material";
import { useData } from "../../utils/useData";
import { LoadingSpinner } from "../../components/shared/LoadingSpinner";
import { useConfig } from "../../utils/useConfig";
import { CardOffering, CardOfferingType, Employer } from "../../models";
import { useFormik } from "formik";
import { LoadingButton } from "@mui/lab";
import { useMutation } from "react-query";
import { toast } from "react-toastify";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { CardDesign, CardDesignType } from "../../models/cards/cardDesign";

const CardManagementContainer = styled(Box)({
  padding: 24,
  borderRadius: 12,
  marginTop: 24,
});

const SectionTitle = styled(Typography)({
  fontWeight: 600,
  fontSize: 16,
  marginBottom: 16
});

const CheckboxGroup = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  marginBottom: 24
});

const CardStatsBox = styled(Box)({
  marginTop: 8,
  backgroundColor: 'rgba(2, 176, 125, 0.1)',
  borderRadius: 8,
  padding: 8,
  width: '100%',
  textAlign: 'center'
});

const SubmitButtonContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'flex-end',
  marginTop: 24
});

const CenteredInfoText = styled(Typography)({
  textAlign: 'left',
  alignContent: "center",
  maxWidth: "50%"
});

const resolveCardDesignUrl = (
        employer: Employer,
        allCardOfferings: CardOffering[],
        cardDesigns: CardDesign[],
        bucketUrl: string,
        offeringType: CardOfferingType
    ) => {
        const employerCardOfferings = employer.generalOptions?.cardOfferings || [];
        const targetOffering = allCardOfferings.find(offering => offering.type === offeringType);

        const employerOffering = employerCardOfferings.find(offering => offering.cardOfferingId === targetOffering?.id);

        if(employerOffering && employerOffering.cardDesignId){
            return (`${bucketUrl}/card-designs/${employerOffering.cardDesignId}/normal.png`);
        } else {
            const employerCardDesign = cardDesigns.find(design => design.type === CardDesignType.EMPLOYER_DEDICATED);
            if(employerCardDesign){
                return (`${bucketUrl}/card-designs/${employerCardDesign.id}/normal.png`);
            }
        }
        

        const employerGeneralCardDesign = cardDesigns.find(design => design.type === CardDesignType.GENERAL);
        if(employerGeneralCardDesign){
            return (`${bucketUrl}/card-designs/${employerGeneralCardDesign.id}/normal.png`);
        }
        
        console.warn("No card design found for offering type: ", offeringType);
        return null;
    }

const CardManagement = () => {
    const [physicalCardsIssued, setPhysicalCardsIssued] = useState(0);
    const [digitalCardsIssued, setDigitalCardsIssued] = useState(0);

    const { employer, cards, allCardOfferings, cardDesigns } = useData();
    const { config } = useConfig();
    const { getToken } = useKindeAuth();

    const [physicalCardDesignUrl, setPhysicalCardDesignUrl] = useState<string | null>(null);
    const [digitalCardDesignUrl, setDigitalCardDesignUrl] = useState<string | null>(null);


    const [initialFormValues, setInitialFormValues] = useState({
        enableDigitalCards: false,
        autoIssueDigitalCards: false,
        enablePhysicalCards: false,
        autoIssuePhysicalCards: false
    });

    useEffect(() => {
        if(
            employer.data && 
            allCardOfferings.data && 
            cardDesigns.data &&
            config &&
            config.PUBLIC_BUCKET_URL
        ){
            var digitalUrl = resolveCardDesignUrl(
                employer.data,
                allCardOfferings.data,
                cardDesigns.data,
                config.PUBLIC_BUCKET_URL,
                CardOfferingType.DIGITAL
            );
            var physicalUrl = resolveCardDesignUrl(
                employer.data,
                allCardOfferings.data,
                cardDesigns.data,
                config.PUBLIC_BUCKET_URL,
                CardOfferingType.PHYSICAL
            );

            setDigitalCardDesignUrl(digitalUrl);
            setPhysicalCardDesignUrl(physicalUrl);
        }
    }, [employer.data, allCardOfferings.data, cardDesigns.data, config, config?.PUBLIC_BUCKET_URL]);

    useEffect(() => {
        if (employer.data && allCardOfferings.data) {
            const employerCardOfferings = employer.data.generalOptions?.cardOfferings || [];
            const digitalOffering = allCardOfferings.data.find(offering => offering.type === CardOfferingType.DIGITAL);
            const physicalOffering = allCardOfferings.data.find(offering => offering.type === CardOfferingType.PHYSICAL);
            
            const hasDigitalOffering = digitalOffering && employerCardOfferings.some(
                offering => offering.cardOfferingId === digitalOffering.id
            );
            
            const hasPhysicalOffering = physicalOffering && employerCardOfferings.some(
                offering => offering.cardOfferingId === physicalOffering.id
            );
            
            let digitalAutoIssue = false;
            let physicalAutoIssue = false;
            
            if (hasDigitalOffering && digitalOffering) {
                const offering = employerCardOfferings.find(o => o.cardOfferingId === digitalOffering.id);
                digitalAutoIssue = Boolean(offering?.autoIssueCard);
            }
            
            if (hasPhysicalOffering && physicalOffering) {
                const offering = employerCardOfferings.find(o => o.cardOfferingId === physicalOffering.id);
                physicalAutoIssue = Boolean(offering?.autoIssueCard);
            }
            
            setInitialFormValues({
                enableDigitalCards: hasDigitalOffering ?? false,
                autoIssueDigitalCards: digitalAutoIssue,
                enablePhysicalCards: hasPhysicalOffering ?? false,
                autoIssuePhysicalCards: physicalAutoIssue
            });
        }
    }, [employer.data, allCardOfferings.data]);

    const addOfferingMutation = useMutation(
      async ({ offeringId, autoIssue }: { offeringId: string, autoIssue: boolean }) => {
        const token = await getToken();

        await fetch(`${config?.API_URL}/employers/${employer.data?.id}/card-offering/${offeringId}`, {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                autoIssueCard: autoIssue
            })
        })

        return { success: true, type: offeringId };
      },
      {
        onSuccess: () => {
          toast.success(`Cards enabled successfully`);
          employer.refetch();
        },
        onError: (error, variables) => {
          console.error(`Error enabling ${variables.offeringId} cards:`, error);
          toast.error(`Failed to enable cards`);
        }
      }
    );

    const removeOfferingMutation = useMutation(
      async ({ offeringId }: { offeringId: string }) => {
        const token = await getToken();

        await fetch(`${config?.API_URL}/employers/${employer.data?.id}/card-offering/${offeringId}`, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        })

        return { success: true, type: offeringId };
      },
      {
        onSuccess: () => {
          toast.success(`Cards disabled successfully`);
          employer.refetch();
          allCardOfferings.refetch();
        },
        onError: (error, variables) => {
          console.error(`Error disabling ${variables.offeringId} cards:`, error);
          toast.error(`Failed to disable cards`);
        }
      }
    );

    const formik = useFormik({
        initialValues: initialFormValues,
        enableReinitialize: true,
        onSubmit: async (values, { setSubmitting }) => {
            try {                
                const promises: Promise<any>[] = [];
                const digitalOffering = allCardOfferings.data?.find(offering => offering.type === CardOfferingType.DIGITAL);
                const physicalOffering = allCardOfferings.data?.find(offering => offering.type === CardOfferingType.PHYSICAL);

                var digitalCardsChanged = values.enableDigitalCards !== initialFormValues.enableDigitalCards;
                var digitalAutoIssueChanged = values.autoIssueDigitalCards !== initialFormValues.autoIssueDigitalCards;
                var physicalCardsChanged = values.enablePhysicalCards !== initialFormValues.enablePhysicalCards;
                var physicalAutoIssueChanged = values.autoIssuePhysicalCards !== initialFormValues.autoIssuePhysicalCards;

                var shouldUpdateDigital = digitalCardsChanged || digitalAutoIssueChanged;
                var shouldUpdatePhysical = physicalCardsChanged || physicalAutoIssueChanged;
                
                if(shouldUpdateDigital) {
                    if(formik.values.enableDigitalCards) {
                        if(!digitalOffering) {
                            toast.error('Digital card offering not found');
                            return;
                        }
                        promises.push(addOfferingMutation.mutateAsync({ offeringId: digitalOffering.id, autoIssue: formik.values.autoIssueDigitalCards }));
                    } else {
                        if(!digitalOffering) {
                            toast.error('Digital card offering not found');
                            return;
                        }
                        promises.push(removeOfferingMutation.mutateAsync({ offeringId: digitalOffering.id }));
                    }    
                }

                if(shouldUpdatePhysical) {
                    if(formik.values.enablePhysicalCards) {
                        if(!physicalOffering) {
                            toast.error('Physical card offering not found');
                            return;
                        }
                        promises.push(addOfferingMutation.mutateAsync({ offeringId: physicalOffering.id, autoIssue: formik.values.autoIssuePhysicalCards }));
                    } else {
                        if(!physicalOffering) {
                            toast.error('Physical card offering not found');
                            return;
                        }
                        promises.push(removeOfferingMutation.mutateAsync({ offeringId: physicalOffering.id }));
                    }    
                }
                
                await Promise.all(promises);
                setSubmitting(false);
            } catch (error) {
                console.error('Error saving card settings:', error);
                setSubmitting(false);
            }
        }
    });

    useEffect(() => {
        let digitalCount = 0;
        let physicalCount = 0;
        
        cards.data?.items.forEach(card => {
            if(card.offering.type === CardOfferingType.DIGITAL) {
                digitalCount += 1;
            } else {
                physicalCount += 1;
            }
        });
        
        setDigitalCardsIssued(digitalCount);
        setPhysicalCardsIssued(physicalCount);
    }, [cards.data]);
    

    const employerIsLoading = employer.isIdle || employer.isLoading;
    const cardsAreLoading = cards.isIdle || cards.isLoading;

    if (employerIsLoading || cardsAreLoading) return <LoadingSpinner />;

    return (
        <Box>
            <form onSubmit={formik.handleSubmit}>
                <CardManagementContainer>
                    <Stack direction={"row"} justifyContent={"space-between"} width={"100%"}>
                        <CheckboxGroup>
                            <SectionTitle>Digital Cards</SectionTitle>
                            <FormControlLabel
                                control={
                                    <Checkbox 
                                        name="enableDigitalCards"
                                        checked={formik.values.enableDigitalCards}
                                        disabled // Todo: Remove this when the app/my portal can support 2+ cards
                                        onChange={(e) => {
                                            formik.handleChange(e);
                                            if (!e.target.checked) {
                                                formik.setFieldValue('autoIssueDigitalCards', false);
                                            }
                                        }}
                                        color="primary"
                                    />
                                }
                                label="Enable digital cards"
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox 
                                        name="autoIssueDigitalCards"
                                        checked={formik.values.autoIssueDigitalCards}
                                        onChange={formik.handleChange}
                                        color="primary"
                                        disabled={!formik.values.enableDigitalCards || true } // Todo: Remove || true when the app/my portal can support 2+ cards
                                    />
                                }
                                label="Auto issue digital cards"
                            />
                        </CheckboxGroup>
                        <CenteredInfoText variant="body2">
                            The digital card lives in your phone’s virtual wallet (like Apple Wallet or Google Wallet), giving you instant access to make secure, contactless payments right from your mobile device. It can be activated and used immediately—no waiting for delivery—and is perfect for fast, on-the-go payments or when you don’t want to carry a physical card.
                        </CenteredInfoText>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            { 
                                digitalCardDesignUrl ?
                                    <img
                                    alt="card"
                                    height={"132px"}                            
                                    src={digitalCardDesignUrl}  
                                /> :
                                <Box display="flex" justifyContent="center" alignItems="center" height="132px">
                                    <CircularProgress />
                                </Box>
                            }
                            <CardStatsBox>
                                <Typography variant="body2" fontWeight={600}>
                                    {digitalCardsIssued} Digital {digitalCardsIssued === 1 ? 'Card' : 'Cards'} Issued
                                </Typography>
                            </CardStatsBox>
                        </Box>
                    </Stack>
                    <Divider sx={{ my: 3 }} />
                    <Stack direction={"row"} justifyContent={"space-between"} width={"100%"}>
                        <CheckboxGroup>
                            <SectionTitle>Physical Cards</SectionTitle>
                            <FormControlLabel
                                control={
                                    <Checkbox 
                                        name="enablePhysicalCards"
                                        checked={formik.values.enablePhysicalCards}
                                        disabled // Todo: Remove this when the app/my portal can support 2+ cards
                                        onChange={(e) => {
                                            formik.handleChange(e);
                                            if (!e.target.checked) {
                                                formik.setFieldValue('autoIssuePhysicalCards', false);
                                            }
                                        }}
                                        color="primary"
                                    />
                                }
                                label="Enable physical cards"
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox 
                                        name="autoIssuePhysicalCards"
                                        checked={formik.values.autoIssuePhysicalCards}
                                        onChange={formik.handleChange}
                                        color="primary"
                                        disabled={!formik.values.enablePhysicalCards || true} // Todo: Remove || true when the app/my portal can support 2+ cards
                                    />
                                }
                                label="Auto issue physical cards"
                            />
                        </CheckboxGroup>
                        <CenteredInfoText variant="body2">
                            Our physical card is a standard payment card that can be used in-store, online, or over the phone—just like any traditional debit or credit card. It’s delivered to a nominated address and can be used anywhere Mastercard is accepted.
                        </CenteredInfoText>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            { 
                                physicalCardDesignUrl ?
                                    <img
                                        alt="card"
                                        height={"132px"}                            
                                        src={physicalCardDesignUrl}
                                    /> :
                                    <Box display="flex" justifyContent="center" alignItems="center" height="132px">
                                        <CircularProgress />
                                    </Box>
                            }
                            <CardStatsBox>
                                <Typography variant="body2" fontWeight={600}>
                                    {physicalCardsIssued} Physical {physicalCardsIssued === 1 ? 'Card' : 'Cards'} Issued
                                </Typography>
                            </CardStatsBox>
                        </Box>
                    </Stack>
                    <SubmitButtonContainer>
                        <LoadingButton 
                            type="submit" 
                            variant="contained" 
                            color="primary"
                            loading={formik.isSubmitting}
                            disabled={!formik.dirty}
                        >
                            Save Changes
                        </LoadingButton>
                    </SubmitButtonContainer>
                </CardManagementContainer>
            </form>
        </Box>
    );
}

export { CardManagement } 